/**
 * Several options on one bid, on the customer's page — "like separate bids in one" (owner, Nov 3 2026).
 *
 * Each option is its own card: a title, a line of copy, its photos or mockups, its priced lines grouped under
 * their own headings with a subtotal each, and one big tick. They can take one, or more than one; the price at the
 * bottom of the page follows the ticks straight away (`priceOf` below is what the bar adds up), and the server
 * prices the same picks again at accept — the page is never trusted with the number that matters.
 *
 * An option's own extras are ticked inside it, and only once the option itself is taken: an extra on an option
 * nobody picked is not a thing they are buying.
 *
 * `extra` draws something under a line — the per-line size picker (a bush at 1, 3 or 15 gallon) hangs there, which
 * is why a size choice lives on the line and not on the option. It is told whether the option is taken, so a page
 * can keep a choice that moves the total out of an option nobody has picked.
 *
 * Server: services/bidOptions → GET /quotes/public/:token `bid_options`.
 */
import React from 'react';
import { Check } from 'lucide-react';
import { money } from '../utils/format';
import { OptionRow } from './public';

export type BidOptionLine = {
  id: number; description: string; quantity: number; unit_price: number; total: number;
  unit?: string | null; cadence?: string | null; is_optional: number; is_selected: number; quote_on_request: number;
};
export type BidOptionSection = { name: string | null; subtotal: number; lines: BidOptionLine[] };
export type BidOption = {
  id: number; title: string; blurb: string | null; is_selected: boolean; subtotal: number;
  media: { id: number; file_name: string | null; mime_type: string; data: string }[];
  sections: BidOptionSection[];
};
export type BidOptionsOffer = { locked: boolean; chosen: number[]; options: BidOption[] };

const per = (li: BidOptionLine) => (li.unit === 'month' ? '/mo' : li.cadence === 'quarterly' ? ' a quarter' : li.cadence === 'yearly' ? '/yr'
  : li.cadence === 'one_time' ? '' : li.unit === 'visit' ? '/visit' : li.unit === 'year' ? '/yr' : '');
const fmtQty = (q: number) => (Number.isInteger(q) ? String(q) : String(Math.round(q * 100) / 100));
const round2 = (n: number) => Math.round(n * 100) / 100;
const linesOf = (o: BidOption) => o.sections.flatMap(s => s.lines);

/**
 * What one option comes to with these extras ticked: its required lines always, its own extras when they are on.
 * The same rule the server uses (services/bidOptions.subtotalOf), so the bar and the bid agree.
 */
export function optionPrice(o: BidOption, ticked: Set<number> | null): number {
  return round2(linesOf(o).reduce((t, li) => {
    if (Number(li.quote_on_request)) return t;
    if (Number(li.is_optional) && !(ticked ? ticked.has(li.id) : Number(li.is_selected) === 1)) return t;
    return t + (Number(li.total) || (Number(li.quantity) || 0) * (Number(li.unit_price) || 0) || 0);
  }, 0));
}

/** What the options they have ticked add to the bid. */
export function priceOf(offer: BidOptionsOffer | null, chosen: Set<number> | null, ticked: Set<number> | null): number {
  if (!offer || !chosen) return 0;
  return round2(offer.options.filter(o => chosen.has(o.id)).reduce((t, o) => t + optionPrice(o, ticked), 0));
}

/** Every optional line inside an option — the page starts its own ticks from the ones already on. */
export function optionalLinesOf(offer: BidOptionsOffer | null): BidOptionLine[] {
  return (offer?.options || []).flatMap(o => linesOf(o).filter(li => Number(li.is_optional)));
}

export default function BidOptions({ offer, chosen, onToggle, ticked, onToggleLine, extra }: {
  offer: BidOptionsOffer;
  /** The options they have taken. */
  chosen: Set<number>;
  onToggle: (id: number) => void;
  /** Their ticks on the optional lines inside the options (shared with the rest of the page). */
  ticked: Set<number> | null;
  onToggleLine: (id: number) => void;
  extra?: (li: BidOptionLine, optionTaken: boolean) => React.ReactNode;
}) {
  const many = offer.options.length > 1;
  return (
    <section className="space-y-4">
      <div className="px-1">
        <h2 className="font-black uppercase tracking-tight text-base leading-tight text-gray-900">
          {many ? `${offer.options.length} ways to do it` : 'What we are offering'}
        </h2>
        {!offer.locked && many && (
          <p className="text-[14px] text-gray-700 mt-1">
            Read them all and tap the one you want — you can take more than one. The price at the bottom keeps up.
          </p>
        )}
      </div>

      {offer.options.map((o, i) => {
        const on = chosen.has(o.id);
        const price = optionPrice(o, ticked);
        return (
          <section key={o.id}
            className={`rounded-3xl border p-5 shadow-soft ${on ? 'border-brand-600 bg-brand-100' : 'border-brand-100 bg-white'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {many && <p className="text-[11px] font-black uppercase tracking-widest text-brand-700">Option {i + 1}</p>}
                <h3 className="text-[19px] font-black text-gray-900 leading-tight">{o.title}</h3>
              </div>
              <p className="text-[19px] font-black text-gray-900 tabular-nums whitespace-nowrap">{money(price)}</p>
            </div>
            {o.blurb && <p className="text-[15px] text-gray-700 mt-2 leading-relaxed whitespace-pre-line">{o.blurb}</p>}

            {o.media.filter(m => String(m.mime_type || 'image/jpeg').startsWith('image/')).length > 0 && (
              <div className={`grid gap-2 mt-4 ${o.media.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {o.media.filter(m => String(m.mime_type || 'image/jpeg').startsWith('image/')).map(m => (
                  <img key={m.id} src={m.data} alt={m.file_name || o.title}
                    className={`w-full object-cover rounded-2xl ${o.media.length === 1 ? 'h-48' : 'h-32'}`} />
                ))}
              </div>
            )}

            <div className="mt-4 space-y-4">
              {o.sections.map((s, si) => (
                <div key={s.name || `s${si}`}>
                  {s.name && (
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-[12px] font-black uppercase tracking-widest text-gray-600">{s.name}</p>
                      <p className="text-[13px] font-bold text-gray-700 tabular-nums">{money(s.subtotal)}</p>
                    </div>
                  )}
                  <ul className="divide-y divide-gray-200/70">
                    {s.lines.map(li => {
                      const isExtra = !!Number(li.is_optional);
                      const lineOn = ticked ? ticked.has(li.id) : Number(li.is_selected) === 1;
                      if (isExtra) {
                        return (
                          <li key={li.id} className="py-2">
                            <OptionRow on={lineOn} ariaLabel={li.description}
                              onClick={() => { if (!offer.locked && on) onToggleLine(li.id); }}>
                              <span className={`h-6 w-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 ${lineOn ? 'bg-brand-600 border-brand-600' : 'border-brand-300'}`}>
                                {lineOn && <Check className="h-4 w-4 text-white" aria-hidden />}
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className="block text-[15px] font-bold text-gray-900">{li.description}</span>
                                <span className="block text-[13px] text-gray-600">
                                  {Number(li.quote_on_request) ? 'Tick it and we will send you a price.' : on ? 'An extra on this option' : 'Take this option first'}
                                </span>
                              </span>
                              <span className={`text-[15px] font-bold tabular-nums whitespace-nowrap ${lineOn ? 'text-brand-700' : 'text-gray-600'}`}>
                                {Number(li.quote_on_request) ? 'Quote on request' : <>+{money(li.total)}<span className="font-semibold">{per(li)}</span></>}
                              </span>
                            </OptionRow>
                            {extra && extra(li, on)}
                          </li>
                        );
                      }
                      return (
                        <li key={li.id} className="py-3">
                          <span className="grid grid-cols-[1fr_auto] gap-x-3 items-baseline w-full">
                            <span className="text-[15px] text-gray-800">
                              {li.description}
                              {Number(li.quantity) !== 1 && (
                                <span className="block text-xs text-gray-600 tabular-nums">{fmtQty(Number(li.quantity))} × {money(li.unit_price)}</span>
                              )}
                            </span>
                            <span className="text-right text-[15px] font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                              {Number(li.quote_on_request)
                                ? <span className="text-[13px] font-medium text-gray-600">Quote on request</span>
                                : <>{money(li.total)}{per(li) && <span className="text-xs font-normal text-gray-500">{per(li)}</span>}</>}
                            </span>
                          </span>
                          {extra && extra(li, on)}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            {offer.locked ? (
              <p className={`mt-4 text-[14px] font-bold ${on ? 'text-brand-700' : 'text-gray-500'}`}>
                {on ? 'This is the one you chose.' : 'Not chosen.'}
              </p>
            ) : (
              <div className="mt-4">
                <OptionRow on={on} ariaLabel={`Take ${o.title}`} onClick={() => onToggle(o.id)}>
                  <span className={`h-6 w-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 ${on ? 'bg-brand-600 border-brand-600' : 'border-brand-300'}`}>
                    {on && <Check className="h-4 w-4 text-white" aria-hidden />}
                  </span>
                  <span className="flex-1 min-w-0 text-[15px] font-bold text-gray-900">
                    {on ? 'This one — in your price' : many ? 'I want this one' : 'Yes, do this'}
                  </span>
                  <span className="text-[15px] font-black text-gray-900 tabular-nums whitespace-nowrap">{money(price)}</span>
                </OptionRow>
              </div>
            )}
          </section>
        );
      })}
    </section>
  );
}
